import React, { useEffect } from 'react'
import { Image, ImageStyle, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StackNavigationProp } from '@react-navigation/stack'
import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { IconSources } from '@src/assets/icons'
import { ScrollViewWithFade } from '@src/components/ScrollViewWithFade'
import { Button, Text } from '@src/components/base'
import { NavigationContainer } from '@src/screens/Common/containers'
import { RootStackParamList } from '@src/navigation/types'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { terraProvidersSelector } from '@src/selectors/integrations'
import { StyleService, useStyleSheet } from '@src/style/service'
import { TerraProviderKind } from '@src/types'
import { useGoBack } from '@src/utils'

const FADE_BUFFER = 24

export const TerraFitbitGoogleHealthAnnouncementScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const providers = useSelector(terraProvidersSelector)
  const googleHealthProvider = providers.find(
    (provider) => provider.providerKind === TerraProviderKind.GoogleHealth,
  )

  useLazyScreenPreload('SupportHub')

  useEffect(() => {
    dispatch({ type: 'settings/fetchTerraProviders' })
  }, [dispatch])

  const onSwitchLaterPress = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'Drawer' }],
    })
  }

  const onSwitchNowPress = () => {
    if (!googleHealthProvider) {
      return
    }

    navigation.navigate('TerraProvider', { provider: googleHealthProvider.provider })
  }

  return (
    <NavigationContainer
      title="Google Health"
      leftIcon={{ pack: 'eva', name: 'close' }}
      goBack={goBack}
    >
      <SafeAreaView style={styles.content} edges={['bottom']}>
        <View style={styles.mainContent}>
          <ScrollViewWithFade scrollViewStyle={styles.scrollViewContent}>
            <Image source={IconSources.googleHealthIcon} style={styles.icon as ImageStyle} />
            <Text type="title-2" style={styles.title}>
              Fitbit is moving to Google Health
            </Text>

            <View style={styles.bodyContainer}>
              <Text type="regular" style={styles.bodyText}>
                Google is shutting down Fitbit and moving all users to Google Health. Fitbit data
                syncing will be disabled permanently starting Sept 30. Switch now to Google Health
                to prevent any data loss.
              </Text>
              <Text type="regular" style={styles.bodyText}>
                Once you switch to Google Health, Nutrisense will backfill any missing data from the
                last 7 days from Google Health. Be sure to sign in with the same Google account you
                use for Google Health.
              </Text>
            </View>
          </ScrollViewWithFade>

          <Text type="regular" style={styles.supportText}>
            Have questions? Reach out to our{' '}
            <Text
              type="regular"
              bold
              style={styles.supportLink}
              onPress={() => navigation.navigate('SupportHub')}
            >
              Support team
            </Text>
          </Text>
        </View>

        <View style={styles.buttons}>
          <Button
            type="primary"
            size="block"
            accessibilityLabel="Switch Now"
            disabled={!googleHealthProvider}
            onPress={onSwitchNowPress}
          >
            Switch Now
          </Button>
          <Button
            type="outline"
            size="block"
            accessibilityLabel="Switch Later"
            onPress={onSwitchLaterPress}
          >
            Switch Later
          </Button>
        </View>
      </SafeAreaView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  content: {
    flex: 1,
    paddingHorizontal: 16,
    backgroundColor: 'theme.background',
  },
  mainContent: {
    flex: 1,
  },
  scrollViewContent: {
    paddingTop: 8,
    paddingBottom: FADE_BUFFER,
  },
  icon: {
    width: 80,
    height: 80,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 24,
  },
  title: {
    textAlign: 'center',
    marginBottom: 24,
  },
  bodyContainer: {
    marginBottom: 24,
    gap: 16,
  },
  bodyText: {
    textAlign: 'center',
  },
  supportText: {
    textAlign: 'center',
    color: 'theme.text.secondary',
    marginBottom: 16,
  },
  supportLink: {
    color: 'theme.text.link',
  },
  buttons: {
    gap: 8,
    marginBottom: 16,
  },
})
